<template>
  <div class="page-manage min">
    <div class="search-area">
      <el-input
        v-model="searchKeyword"
        placeholder="뷰저블 계정을 입력하세요."
        clearable
        size="large"
        class="input-search"
        @clear="clearSearch"
        @keyup.enter="handleSearch"
      >
        <template #append>
          <el-button
            aria-label="검색"
            @click="handleSearch"
          >
            <font-awesome-icon icon="fa-solid fa-magnifying-glass" />
          </el-button>
        </template>
      </el-input>
      <el-button
        type="info"
        size="large"
        class="btn-reset"
        @click="handleReset"
      >
        <font-awesome-icon icon="fa-solid fa-rotate-left" />
      </el-button>
      <p 
        v-if="isResult"
        class="result"
      >
        <strong>{{ `${data[0].companyName}(${data[0].userId})` }}</strong>의 플랜이 총 <strong>{{  totalCount }}</strong> 개 있습니다.
      </p>
    </div>
    <template v-if="!loading && !isError">
      <el-table
        :data="data"
        style="width: 800px"
        ref="tableRef"
        @sort-change="handleSortChange"
        @filter-change="handleFilterChange"
      >
        <el-table-column
          prop="requestId"
          label="리퀘스트 ID"
          width="200"
          align="center"
        />
        <el-table-column
          prop="status"
          label="플랜 상태"
          width="120"
          align="center"
          column-key="status"
          :filters="[
            { text: '대기', value: 'waiting' },
            { text: '적용중', value: 'use' },
            { text: '종료', value: 'complete' }
          ]"
        >
          <template #default="scope">
            <status-tag 
              v-if="scope.row.status === 'waiting'"
              color="gray"
              size="small"
              text="대기"
            />
            <status-tag 
              v-if="scope.row.status === 'use'"
              color="blue"
              size="small"
              text="적용중"
            />
            <status-tag 
              v-if="scope.row.status === 'complete'"
              color="gray_dark"
              size="small"
              text="종료"
            />
          </template>
        </el-table-column>
        <el-table-column
          label="플랜명"
          width="160"
          align="center"
        >
          <template #default="scope">
            <span 
              v-if="scope.row.beusPlanNumber"
              class="plan is-center"
            >
              <img 
                :src="IconBeusGray" 
                alt="히트맵 플랜"
              />
                {{  getBeusPlanLName(Number(scope.row.beusPlanNumber)) }}
            </span>
            <span 
              v-if="scope.row.baPlanNumber"
              class="plan is-center"
            >
              <img 
                :src="IconBiGray" 
                alt="저니맵 플랜"
              />
                {{  getBaPlanLName(Number(scope.row.baPlanNumber)) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          prop="useStartDate"
          label="사용 시작일"
          width="160"
          align="center"
          sortable="custom"
        />
        <el-table-column
          prop="useEndDate"
          label="사용 종료일"
          width="160"
          align="center"
          sortable="custom"
        />
        <template #empty>
          <div class="placeholder-message">
            <p v-if="!searchKeyword">
              검색어를 입력하세요.
            </p>
            <p v-else>
              검색 결과가 없습니다.
            </p>
          </div>
        </template>
      </el-table>
      <div class="pagination-area is-small">
        <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 30, 50]"
          :total="totalCount"
          layout="sizes, prev, pager, next"
          class="small"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </template>
    <template v-else-if="loading">
      <common-loader/>
    </template>
    <template v-else-if="isError && !loading">
      <CommonError message="서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.<br/> 문제가 지속되면 관리자에게 문의하세요."/>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted } from 'vue';
import { useRoute, useRouter } from "vue-router";
import { Plan } from '@/types/Plan';
import { getPlanList } from '@/api';
import { getBeusPlanLName, getBaPlanLName } from "@/utils";
import { ElMessageBox } from 'element-plus';
import type { TableInstance } from 'element-plus';
import StatusTag from '@/components/StatusTag.vue';
import IconBeusGray from '@/assets/images/ico-beus-g.svg';
import IconBiGray from '@/assets/images/ico-bi-g.svg';
import CommonLoader from '@/components/CommonLoader.vue';
import CommonError from '@/components/CommonError.vue';

const route = useRoute();
const router = useRouter();

const data = ref<Plan[]>([]);
const totalCount = ref<number>(0);
const loading = ref<boolean>(false);
const isError = ref<boolean>(false);
const isResult = ref(false);
const searchKeyword = ref('');
const clearSearch = () => {
  searchKeyword.value = '';
};
const currentPage = ref<number>(1);
const pageSize = ref<number>(30);
const sortValue = ref<string | undefined>(undefined);
const selectedStatus = ref<string[] | undefined>([]);

const fetchData = async () => {
  loading.value = true;
  const params: Record<string, any> = {
    userId: searchKeyword.value,
    pageNum: currentPage.value,
    pageSize: pageSize.value,
  }

  if (sortValue.value) {
    params.sort = sortValue.value;
  }

  if (selectedStatus.value && selectedStatus.value.length > 0) {
    params.search = { status: selectedStatus.value };
  }

  // console.log('plan payload:',params);
  try {
    const response = await getPlanList(params);
    if(response.success) {
      // console.log('result',response.result);

      if (response.result.count === 0) {
        ElMessageBox.alert('검색결과가 없습니다. 검색 조건을 변경해주세요.', {
          confirmButtonText: '확인',
        });
        return;
      } else {
        totalCount.value = response.result.count;
        data.value = response.result.list;
      }
    }
    isResult.value = true;
  } catch (error) {
    loading.value = false;
    isError.value = true;
    console.error('Error Message:', error)
  } finally {
    loading.value = false;
  }
}

const handleSearch = async () =>  {
  if(searchKeyword.value.length < 2) {
    ElMessageBox.alert('검색어는 2자 이상 입력해주세요.', {
      confirmButtonText: '확인',
    });
    return;
  }
  router.replace({ path: '/request/plan', query: { param: searchKeyword.value } });
}

const handleSizeChange = (size: number) => {
  pageSize.value = size;
  fetchData();
}
const handlePageChange = (page: number) => {
  currentPage.value = page;
  fetchData();
};

watch(
  () => route.query.param,
  (newParam) => {
    if (newParam) {
      searchKeyword.value = newParam as string;
      fetchData();
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (route.query.param) {
    searchKeyword.value = route.query.param as string;
    fetchData();
  }
});

const handleSortChange = ({ prop, order }: { prop: string; order: string }) => {
  if (order === "ascending") {
    sortValue.value = prop;
  } else if (order === "descending") {
    sortValue.value = `-${prop}`
  } else {
    sortValue.value = undefined;
  }
  fetchData();
};

const handleFilterChange = (filters: Record<string, string[]>) => {
  selectedStatus.value = filters.status || [];
  fetchData();
};

const tableRef = ref<TableInstance>()

const handleReset = () => {
  currentPage.value = 1;
  pageSize.value = 30;
  tableRef.value?.clearFilter();
  tableRef.value?.clearSort();
  sortValue.value = undefined;
  selectedStatus.value = undefined;
  fetchData(); 
}

</script>